import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {catchError, map, mergeMap, switchMap} from 'rxjs/operators';
import {concat, of, throwError} from 'rxjs';

import { OrderService } from 'libs/order/src/lib/services';

import { TreatmentsService } from '../services/treatments.service';
import {
	AssignAndGoToTreatment,
	InitiateSubsequentOrder,
	OrderNextPackage,
	RejectRefinementRequest,
	ReleaseRefinementRequest,
	RequestRefinement,
	RequestRefinementRelease,
	SaveDashboardState,
	SubmitComplaint,
	SubmitComplaintSuccess,
	SubmitReleaseRequest,
	SubmitReleaseRequestOrcadent,
	TreatmentActionTypes,
	TreatmentRequested,
	UpsertTreatment,
	UpsertTreatmentOnDb
} from './treatment.actions';
import { MessagesRequested, UpsertMessage } from 'libs/messages/src/lib/store/messages.actions';
import { CaseWorker, TreatmentModel, TreatmentStatus } from '../models/treatment.model';
import { GoAction } from 'libs/tm-common/src/lib/store/actions/actions';
import { NotificationService } from 'libs/tm-common/src/lib/services/notification.service';
import { ParentTreatmentSelectedAction, PatientSelectedAction, ProductSelectedAction } from 'libs/anamnese/src/lib/store/anamnese.actions';
import { NavigationExtras } from '@angular/router';

@Injectable()
export class TreatmentEffects {

	treatment$ = createEffect(() => this.actions$.pipe(
		ofType<TreatmentRequested>(TreatmentActionTypes.TreatmentRequested),
		mergeMap(({ payload }) => this.treatmentsService.getTreatment(payload.id)),
		map((treatment: TreatmentModel) => new UpsertTreatment({ treatment })),
		catchError(err => {
			console.error('error loading treatment');
			return throwError(() => new Error(err));
		})
	));

	treatmentUpdateDb$ = createEffect(() => this.actions$.pipe(
		ofType<UpsertTreatmentOnDb>(TreatmentActionTypes.UpsertTreatmentOnDb),
		mergeMap(( {payload} ) => this.treatmentsService.updateTreatment(payload.treatmentId, payload.diff)),
		map((treatment: TreatmentModel) => new UpsertTreatment({treatment})),
		catchError(err => {
		 	console.error('error while updating treatment');
		 	return throwError( () => new Error(err));
		})
	));

	orderNextPackage$ = createEffect(() => this.actions$.pipe(
		ofType<OrderNextPackage>(TreatmentActionTypes.OrderNextPackage),
		map( action => action.payload),
		switchMap( ({treatmentId, packageType, interimScanOption}) => this.orderService.alignerFollowupOrder(treatmentId, packageType, interimScanOption)),
		map( treatment => new UpsertTreatment({treatment})),
		catchError(err => {
			console.error('error while updating treatment');
			return throwError( () => new Error(err));
		})
	));

	requestRefinement$ = createEffect(() => this.actions$.pipe(
		ofType<RequestRefinement>(TreatmentActionTypes.RequestRefinement),
		map( action => action.payload),
		switchMap( ({treatmentId, comment}) => this.orderService.requestRefinement(treatmentId, comment)),
		map( treatment => new UpsertTreatment({treatment})),
		catchError(err => {
			return throwError( () => new Error(err));
		})
	));

	requestRefinementRelease$ = createEffect(() => this.actions$.pipe(
		ofType<RequestRefinementRelease>(TreatmentActionTypes.RequestRefinementRelease),
		map( action => action.payload),
		switchMap( ({treatmentId, plannedAligners}) => this.orderService.requestRefinementRelease(treatmentId, plannedAligners)),
		switchMap((treatment: TreatmentModel) => [
			new UpsertTreatment({ treatment }),
			new MessagesRequested()
		]),
		catchError(err => {
			return throwError( () => new Error(err));
		})
	));

	releaseRefinementRequest$ = createEffect(() => this.actions$.pipe(
		ofType<ReleaseRefinementRequest>(TreatmentActionTypes.ReleaseRefinementRequest),
		map( action => action.payload),
		switchMap( ({treatmentId}) => this.orderService.releaseRefinementRequest(treatmentId)),
		map( treatment => new UpsertTreatment({treatment})),
		catchError(err => {
			return throwError( () => new Error(err));
		})
	));

	rejectRefinementRequest$ = createEffect(() => this.actions$.pipe(
		ofType<RejectRefinementRequest>(TreatmentActionTypes.RejectRefinementRequest),
		map( action => action.payload),
		switchMap( ({treatmentId}) => this.orderService.rejectRefinementRequest(treatmentId)),
		map( treatment => new UpsertTreatment({treatment})),
		catchError(err => {
			return throwError( () => new Error(err));
		})
	));

	initiateSubsequentOrder$ = createEffect(() => this.actions$.pipe(
		ofType<InitiateSubsequentOrder>(TreatmentActionTypes.InitiateSubsequentOrder),
		switchMap(({ payload }) => {
			const extras: NavigationExtras = {
				info: {isProfessionalSubsequentOrder: true}
			}
			return concat(
				of(new PatientSelectedAction(payload.patientId)),
				of(new ProductSelectedAction({key:payload.productKey})),
				of(new ParentTreatmentSelectedAction({ parentTreatmentId: payload.treatmentId })),
				of(new GoAction({ path: [`/anamnese`], extras: extras })),
			);
		}),
		catchError(err => {
			console.error('error while initiating subsequent ordering process');
			return throwError( () => new Error(err));
		})
	));

	assignAndGoToTreatment$ = createEffect(() => this.actions$.pipe(
		ofType<AssignAndGoToTreatment>(TreatmentActionTypes.AssignAndGoToTreatment),
		switchMap(({ payload }) => {
			return concat(
				of(new SaveDashboardState({ tab: payload.tab, state: payload.state })),
				of(new UpsertTreatmentOnDb({ treatmentId: payload.treatment._id, diff: { caseworker: payload.username } })),
				of(new GoAction({ path: [`/treatment/edit/${payload.treatment._id}`] })),
			);
		}),
		catchError(err => {
			console.error('error while updating treatment');
			return throwError( () => new Error(err));
		})
	));

	submitComplaint$ = createEffect(() => this.actions$.pipe(
		ofType<SubmitComplaint>(TreatmentActionTypes.SubmitComplaint),
		mergeMap(({ payload }) => this.treatmentsService.updateTreatment(payload.treatment._id, { status: TreatmentStatus.ComplaintReceived, caseworker: CaseWorker.REKLAMATION })),
		switchMap((treatment: TreatmentModel) => [
			new UpsertTreatment({ treatment }),
			new SubmitComplaintSuccess({ treatment })
		]),

		catchError(err => {
			console.error('error while updating treatment');
			return throwError(() => new Error(err));
		})
	));


	submitReleaseRequest$ = createEffect(() => this.actions$.pipe(
		ofType<SubmitReleaseRequest>(TreatmentActionTypes.SubmitReleaseRequest),
		mergeMap(({ payload }) => this.treatmentsService.submitReleaseRequest(payload.treatmentId, payload.releaseRequest)),
		switchMap(({ treatment, message }) => [
			...(message ? [new UpsertMessage({ message })] : []), // Message object can be missing, when no comment was included.
			new UpsertTreatment({ treatment })
		]),
		catchError(err => {
			this.notificationService.showErrorMessage(
				'ADMIN.requestRelease-error',
				(err.status && err.statusText) ? `${err.status} ${err.statusText}: ${err.error?.message ?? err.message}` : err.message
			);
			return throwError(() => new Error(err));
		})
	));

	submitReleaseRequestOrcadent$ = createEffect(() => this.actions$.pipe(
		ofType<SubmitReleaseRequestOrcadent>(TreatmentActionTypes.SubmitReleaseRequestOrcadent),
		mergeMap(({ payload }) => this.treatmentsService.submitReleaseRequestOrcadent(payload.treatmentId)),
		switchMap(({ treatment, message }) => [
			...(message ? [new UpsertMessage({ message })] : []), // Message object can be missing, when no comment was included.
			new UpsertTreatment({ treatment })
		]),
		catchError(err => {
			this.notificationService.showErrorMessage(
				'ADMIN.requestRelease-error',
				(err.status && err.statusText) ? `${err.status} ${err.statusText}: ${err.error?.message ?? err.message}` : err.message
			);
			return throwError(() => new Error(err));
		})
	));

	constructor(
		private actions$: Actions,
		private orderService: OrderService,
		private treatmentsService: TreatmentsService,
		private readonly notificationService: NotificationService
	) {
	}
}
